import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Play, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TrailerModal } from './TrailerModal';
import { useAuth } from '@/hooks/useAuth';
import { createSeriesUrl } from '@/lib/slugs';
import { cn } from '@/lib/utils';
import { getCropStyles, isValidCrop } from '@/lib/crop-utils';
import type { CropData } from '@/lib/crop-utils';


interface Instrument {
  id: string;
  name: string;
  slug: string;
}

interface Artist {
  id: string;
  name: string;
  profile_image_url: string | null;
  crop_homepage?: unknown;
}

interface SeriesWithArtists {
  id: string;
  title: string;
  thumbnail_url: string | null;
  trailer_vimeo_id: string | null;
  artists: Artist[];
}

export function FindLessonSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [selectedInstrument, setSelectedInstrument] = useState<Instrument | null>(null);
  const [series, setSeries] = useState<SeriesWithArtists | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [trailerModalOpen, setTrailerModalOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Staggered reveal states - fade in first
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [showArtist, setShowArtist] = useState(false);
  
  // Delayed border radius states - after fade in
  const [radiusThumbnail, setRadiusThumbnail] = useState(false);
  const [radiusInfoBox, setRadiusInfoBox] = useState(false);
  const [radiusArtist, setRadiusArtist] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Staggered fade in
          setTimeout(() => setShowThumbnail(true), 300);
          setTimeout(() => setShowInfoBox(true), 600);
          setTimeout(() => setShowArtist(true), 900);
          // Delayed border radius - after each fade in completes
          setTimeout(() => setRadiusThumbnail(true), 700);
          setTimeout(() => setRadiusInfoBox(true), 1000);
          setTimeout(() => setRadiusArtist(true), 1300);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fetch primary instruments on mount
  useEffect(() => {
    const fetchInstruments = async () => {
      const { data: tagsData } = await supabase
        .from('tags')
        .select('id, name, slug')
        .eq('type', 'primary_instrument');

      if (tagsData && tagsData.length > 0) {
        // Find which instruments have published videos in a series
        const { data: publishedVideos } = await supabase
          .from('videos')
          .select('id')
          .eq('is_published', true)
          .not('series_id', 'is', null);

        if (publishedVideos && publishedVideos.length > 0) {
          const { data: videoTags } = await supabase
            .from('video_tags')
            .select('tag_id')
            .in('video_id', publishedVideos.map(v => v.id))
            .in('tag_id', tagsData.map(t => t.id));

          const activeTagIds = new Set(videoTags?.map(vt => vt.tag_id) || []);
          const activeInstruments = tagsData.filter(t => activeTagIds.has(t.id));
          setInstruments(activeInstruments);
          if (activeInstruments.length > 0) {
            setSelectedInstrument(activeInstruments[0]);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchInstruments();
  }, []);

  // Fetch a series when instrument changes
  useEffect(() => {
    if (!selectedInstrument) return;

    const fetchSeries = async () => {
      setIsTransitioning(true);
      setShowThumbnail(false);
      setShowInfoBox(false);
      setShowArtist(false);
      setRadiusThumbnail(false);
      setRadiusInfoBox(false);
      setRadiusArtist(false);
      
      // Find published videos with this instrument tag that belong to a series
      const { data: videoTags } = await supabase
        .from('video_tags')
        .select('video_id')
        .eq('tag_id', selectedInstrument.id);

      if (!videoTags?.length) {
        setSeries(null);
        setLoading(false);
        setIsTransitioning(false);
        return;
      }

      const { data: videos } = await supabase
        .from('videos')
        .select('series_id')
        .in('id', videoTags.map(vt => vt.video_id))
        .eq('is_published', true)
        .not('series_id', 'is', null);

      if (!videos?.length) {
        setSeries(null);
        setLoading(false);
        setIsTransitioning(false);
        return;
      }

      const seriesIds = [...new Set(videos.map(v => v.series_id))];

      // Try to find one with a trailer first
      const { data: seriesData } = await supabase
        .from('series')
        .select('id, title, thumbnail_url, trailer_vimeo_id, artist_id')
        .in('id', seriesIds)
        .eq('is_published', true)
        .not('trailer_vimeo_id', 'is', null)
        .limit(1)
        .maybeSingle();

      const chosen = seriesData || (await supabase
        .from('series')
        .select('id, title, thumbnail_url, trailer_vimeo_id, artist_id')
        .in('id', seriesIds)
        .eq('is_published', true)
        .limit(1)
        .maybeSingle()).data;

      if (chosen) {
        // Fetch artists
        const { data: seriesArtistsData } = await supabase
          .from('series_artists')
          .select('artist_id, position')
          .eq('series_id', chosen.id)
          .order('position', { ascending: true });

        let artists: Artist[] = [];
        if (seriesArtistsData?.length) {
          const artistIds = seriesArtistsData.map(sa => sa.artist_id);
          const { data: artistsData } = await supabase
            .from('artists')
            .select('id, name, profile_image_url, crop_homepage')
            .in('id', artistIds);
          if (artistsData) {
            artists = artistIds
              .map(id => artistsData.find(a => a.id === id))
              .filter((a): a is NonNullable<typeof a> => a !== undefined) as Artist[];
          }
        } else if (chosen.artist_id) {
          const { data: artistData } = await supabase
            .from('artists')
            .select('id, name, profile_image_url, crop_homepage')
            .eq('id', chosen.artist_id)
            .maybeSingle();
          if (artistData) artists = [artistData];
        }

        setSeries({ ...chosen, artists });
      } else {
        setSeries(null);
      }
      
      setLoading(false);
      setTimeout(() => {
        setIsTransitioning(false);
        setTimeout(() => setShowThumbnail(true), 100);
        setTimeout(() => setShowInfoBox(true), 300);
        setTimeout(() => setShowArtist(true), 500);
        setTimeout(() => setRadiusThumbnail(true), 500);
        setTimeout(() => setRadiusInfoBox(true), 700);
        setTimeout(() => setRadiusArtist(true), 900);
      }, 100);
    };

    fetchSeries();
  }, [selectedInstrument]);

  const handleInstrumentSelect = (inst: Instrument) => {
    if (inst.id !== selectedInstrument?.id) {
      setSelectedInstrument(inst);
    }
  };

  return (
    <section 
      ref={sectionRef}
      className="py-8 md:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-background"
    >
      <div className="max-w-6xl mx-auto">
        {/* Headline with Dropdown */}
        <div 
          className="mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 600ms ease-out, transform 600ms ease-out',
          }}
        >
          <h2 className="text-hero-smaller leading-tight">
            FIND THE
            <br />
            RIGHT LESSON
            <br />
            FOR{' '}
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 sm:gap-2 outline-none group overflow-visible">
                <span 
                  className="relative pr-1"
                  style={{
                    background: 'linear-gradient(90deg, #CB5EFF 0%, #D84632 50%, #FFFF00 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {(selectedInstrument?.name || 'SELECT').toUpperCase()}
                  <span 
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{
                      background: 'linear-gradient(90deg, #CB5EFF 0%, #D84632 50%, #FFFF00 100%)',
                    }}
                  />
                </span>
                <ChevronDown 
                  className={cn(
                    "w-6 h-6 sm:w-8 sm:h-8 text-foreground transition-transform duration-300 ease-out flex-shrink-0",
                    isDropdownOpen && "rotate-180"
                  )} 
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="bg-card border-border z-50 min-w-[280px] p-3 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
                align="start"
                sideOffset={8}
              >
                {instruments.map((inst) => (
                  <DropdownMenuItem 
                    key={inst.id}
                    onClick={() => handleInstrumentSelect(inst)}
                    className={cn(
                      "cursor-pointer py-4 px-5 font-bold transition-all duration-150 rounded-lg focus:bg-black focus:text-white",
                      inst.id === selectedInstrument?.id 
                        ? "bg-black text-white" 
                        : "hover:bg-black hover:text-white bg-transparent"
                    )}
                    style={{ fontSize: '28px', lineHeight: '1.2' }}
                  >
                    <span
                      className="font-bold uppercase"
                      style={{
                        background: 'linear-gradient(90deg, #CB5EFF 0%, #D84632 50%, #FFFF00 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {inst.name}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </h2>
        </div>

        {/* Series Display */}
        <div 
          className={cn(
            "transition-all duration-500 ease-out",
            isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
          )}
        >
          {series && !loading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left - Series Thumbnail - 25% reduced: 800->600, 600->450, 500->375, 400->300 */}
              <div 
                className="relative overflow-hidden h-[300px] sm:h-[375px] md:h-[450px] lg:h-[600px]"
                style={{
                  opacity: showThumbnail ? 1 : 0,
                  transform: showThumbnail ? 'translateY(0)' : 'translateY(30px)',
                  borderTopRightRadius: radiusThumbnail ? 'clamp(60px, 10vw, 140px)' : '0px',
                  borderBottomRightRadius: radiusThumbnail ? 'clamp(60px, 10vw, 140px)' : '0px',
                  transition: 'opacity 500ms ease-out, transform 500ms ease-out, border-top-right-radius 600ms cubic-bezier(0.22, 1, 0.36, 1), border-bottom-right-radius 600ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                {series.thumbnail_url ? (
                  <img 
                    src={series.thumbnail_url} 
                    alt={series.title}
                    className="w-full h-full object-cover grayscale"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <span className="text-muted-foreground">No thumbnail</span>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-4">
                {/* Top Right - Series Info Box - 324px on desktop */}
                <div 
                  className="p-8 flex flex-col items-center justify-center text-center h-[200px] sm:h-[240px] md:h-[280px] lg:h-[324px]"
                  style={{
                    opacity: showInfoBox ? 1 : 0,
                    transform: showInfoBox ? 'translateY(0)' : 'translateY(30px)',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)',
                    borderTopLeftRadius: radiusInfoBox ? 'clamp(60px, 10vw, 140px)' : '0px',
                    borderBottomLeftRadius: radiusInfoBox ? 'clamp(60px, 10vw, 140px)' : '0px',
                    borderBottomRightRadius: radiusInfoBox ? 'clamp(60px, 10vw, 140px)' : '0px',
                    transition: 'opacity 500ms ease-out, transform 500ms ease-out, border-top-left-radius 600ms cubic-bezier(0.22, 1, 0.36, 1), border-bottom-left-radius 600ms cubic-bezier(0.22, 1, 0.36, 1), border-bottom-right-radius 600ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                >
                  <h3 className="text-headline-3 text-foreground mb-2">
                    {series.title}
                  </h3>
                  {series.artists.length > 0 && (
                    <p className={`text-paragraph-1 text-foreground ${series.trailer_vimeo_id ? 'mb-6' : ''}`}>
                      with {series.artists.map(a => a.name).join(', ')}
                    </p>
                  )}
                  {series.trailer_vimeo_id ? (
                    <Button
                      variant="outline"
                      size="cta"
                      className="text-button text-foreground border-foreground hover:bg-foreground/10 transition-all duration-300"
                      onClick={() => setTrailerModalOpen(true)}
                    >
                      WATCH TRAILER
                      <Play className="w-4 h-4 ml-2 fill-current" />
                    </Button>
                  ) : user ? (
                    <Button
                      variant="outline"
                      size="cta"
                      className="text-button text-foreground border-foreground hover:bg-foreground/10 transition-all duration-300 mt-6"
                      onClick={() => navigate(createSeriesUrl(series.id, series.title))}
                    >
                      VIEW SERIES
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : null}
                </div>

                {/* Bottom Right - Artist Image (hidden on mobile) */}
                <div 
                  className="relative overflow-hidden h-[150px] sm:h-[187px] md:h-[225px] lg:h-[262px] hidden md:block"
                  style={{
                    opacity: showArtist ? 1 : 0,
                    transform: showArtist ? 'translateY(0)' : 'translateY(30px)',
                    borderBottomLeftRadius: radiusArtist ? 'clamp(60px, 10vw, 140px)' : '0px',
                    transition: 'opacity 500ms ease-out, transform 500ms ease-out, border-bottom-left-radius 600ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                >
                  {series.artists[0]?.profile_image_url ? (
                    <img 
                      src={series.artists[0].profile_image_url} 
                      alt={series.artists[0].name}
                      className="w-full h-full object-cover grayscale"
                      style={getCropStyles(
                        isValidCrop(series.artists[0].crop_homepage) 
                          ? (series.artists[0].crop_homepage as CropData) 
                          : null
                      )}
                    />
                  ) : (
                    <div 
                      className="w-full h-full"
                      style={{
                        background: 'linear-gradient(135deg, rgba(203, 94, 255, 0.3) 0%, rgba(216, 70, 50, 0.3) 50%, rgba(255, 255, 0, 0.2) 100%)',
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-[300px] sm:h-[375px] md:h-[450px] lg:h-[600px] bg-muted/20 animate-pulse rounded-r-[60px] sm:rounded-r-[100px] lg:rounded-r-[140px]" />
            <div className="flex flex-col gap-4">
              <div className="h-[200px] sm:h-[240px] md:h-[280px] lg:h-[324px] bg-muted/20 animate-pulse rounded-l-[60px] sm:rounded-l-[100px] lg:rounded-l-[140px] rounded-br-[60px] sm:rounded-br-[100px] lg:rounded-br-[140px]" />
              <div className="h-[150px] sm:h-[187px] md:h-[225px] lg:h-[262px] bg-muted/20 animate-pulse rounded-bl-[60px] sm:rounded-bl-[100px] lg:rounded-bl-[140px] hidden md:block" />
            </div>
          </div>
        )}

        {/* No Series Found */}
        {!loading && !series && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No series found for this instrument.</p>
          </div>
        )}
      </div>

      {/* Trailer Modal */}
      <TrailerModal
        isOpen={trailerModalOpen}
        onClose={() => setTrailerModalOpen(false)}
        vimeoId={series?.trailer_vimeo_id || null}
        title={series?.title || ''}
      />
    </section>
  );
}